import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import { Button } from './ui/button';
import { Card } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { toast } from 'sonner';
import { CheckCircle, Upload, Mail, User, DollarSign, Calendar, FileText } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PaymentConfirm = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  
  const [formData, setFormData] = useState({
    student_email: '',
    payer_name: '',
    payer_email: '',
    package_type: '',
    amount: '',
    paid_at: '',
    reference: '',
    proof_file: null
  });

  useEffect(() => {
    const packageType = searchParams.get('package');
    const studentId = searchParams.get('student_id');
    const amount = searchParams.get('amount');
    
    if (!packageType || !studentId || !amount) {
      toast.error('Invalid payment confirmation link');
      navigate('/');
      return;
    }

    setFormData(prev => ({
      ...prev,
      package_type: packageType,
      amount: amount,
      student_id: studentId
    }));

    // Try to get student email from session
    const studentData = sessionStorage.getItem('student_data');
    if (studentData) {
      const student = JSON.parse(studentData);
      setFormData(prev => ({
        ...prev,
        student_email: student.email
      }));
    }
  }, [searchParams, navigate]);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setFormData(prev => ({ ...prev, proof_file: file }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.student_email || !formData.payer_name || !formData.paid_at) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const submitData = new FormData();
      submitData.append('student_email', formData.student_email);
      submitData.append('payer_name', formData.payer_name);
      submitData.append('payer_email', formData.payer_email || '');
      submitData.append('package_type', formData.package_type);
      submitData.append('amount', formData.amount);
      submitData.append('paid_at', formData.paid_at);
      submitData.append('reference', formData.reference || '');
      
      if (formData.proof_file) {
        submitData.append('proof_file', formData.proof_file);
      }

      const response = await axios.post(`${API}/payment-confirmations`, submitData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      toast.success('Payment confirmation submitted successfully!');
      
      // Redirect to payment received page
      navigate('/payment-received', { 
        state: { 
          packageType: formData.package_type,
          amount: formData.amount 
        }
      });
    } catch (error) {
      console.error('Payment confirmation error:', error);
      toast.error(error.response?.data?.detail || 'Failed to submit payment confirmation');
    } finally {
      setLoading(false);
    }
  };

  const getPackageLabel = () => {
    switch(formData.package_type) {
      case 'enrollment': return 'Enrollment Fee';
      case 'foundation': return 'Foundation Course';
      case 'pro': return 'Pro Course';
      case 'enrollment_foundation': return 'Enrollment + Foundation Course';
      case 'enrollment_pro': return 'Enrollment + Pro Course';
      default: return 'Payment';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-25 to-indigo-50">
      {/* Header */}
      <div className="w-full">
        <img 
          src="https://customer-assets.emergentagent.com/job_bookcritique-1/artifacts/csgwpycc_train%20to%20become%20a%20video%20review%20specialist.gif"
          alt="Train to Become a Video Review Specialist"
          className="w-full h-48 md:h-64 lg:h-80 object-cover object-center"
        />
        <div className="bg-white py-6">
          <div className="container mx-auto px-4 text-center">
            <h1 className="text-3xl md:text-4xl font-bold text-slate-800">
              Confirm Your Payment
            </h1>
          </div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-12">
        <div className="max-w-2xl mx-auto">
          <Card className="card">
            <div className="mb-6">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Payment Details</h2>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span className="text-slate-700">Package:</span>
                  <span className="font-semibold text-blue-700">{getPackageLabel()}</span>
                </div>
                <div className="flex justify-between items-center mt-2">
                  <span className="text-slate-700">Amount Paid:</span>
                  <span className="text-2xl font-bold text-blue-700">£{formData.amount}</span>
                </div>
              </div>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Student Email */}
              <div>
                <Label htmlFor="student_email">Your Email Address *</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="student_email"
                    type="email"
                    value={formData.student_email}
                    onChange={(e) => handleChange('student_email', e.target.value)}
                    placeholder="Email you registered with"
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Must match your registration email</p>
              </div>

              {/* Payer Name */}
              <div>
                <Label htmlFor="payer_name">Payer Full Name *</Label>
                <div className="relative mt-1">
                  <User className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="payer_name"
                    type="text"
                    value={formData.payer_name}
                    onChange={(e) => handleChange('payer_name', e.target.value)}
                    placeholder="Name of person who made payment"
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">This may be different if someone paid on your behalf</p>
              </div>

              {/* Payer Email (Optional) */}
              <div>
                <Label htmlFor="payer_email">Payer Email (Optional)</Label>
                <div className="relative mt-1">
                  <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="payer_email"
                    type="email"
                    value={formData.payer_email}
                    onChange={(e) => handleChange('payer_email', e.target.value)}
                    placeholder="Payer's email (if different)"
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Payment Date/Time */}
              <div>
                <Label htmlFor="paid_at">Date & Time of Payment *</Label>
                <div className="relative mt-1">
                  <Calendar className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="paid_at"
                    type="datetime-local"
                    value={formData.paid_at}
                    onChange={(e) => handleChange('paid_at', e.target.value)}
                    className="pl-10"
                    disabled={loading}
                    required
                  />
                </div>
              </div>

              {/* Reference / Transaction ID */}
              <div>
                <Label htmlFor="reference">Revolut Transaction ID / Reference (Optional)</Label>
                <div className="relative mt-1">
                  <FileText className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                  <Input
                    id="reference"
                    type="text"
                    value={formData.reference}
                    onChange={(e) => handleChange('reference', e.target.value)}
                    placeholder="Transaction ID or reference number"
                    className="pl-10"
                    disabled={loading}
                  />
                </div>
              </div>

              {/* Proof Upload */}
              <div>
                <Label htmlFor="proof_file">Upload Payment Proof (Optional but Recommended)</Label>
                <div className="mt-1">
                  <Input
                    id="proof_file"
                    type="file"
                    accept="image/*,.pdf"
                    onChange={handleFileChange}
                    disabled={loading}
                    className="cursor-pointer"
                  />
                </div>
                <p className="text-xs text-slate-500 mt-1">Screenshot of payment confirmation (JPG, PNG, PDF)</p>
              </div>

              {/* Submit Button */}
              <Button 
                type="submit" 
                disabled={loading}
                className="w-full btn btn-primary"
              >
                {loading ? (
                  'Submitting...'
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Submit Payment Confirmation
                  </>
                )}
              </Button>
            </form>

            <div className="mt-6 text-center text-sm text-slate-600">
              <p>Your confirmation will be reviewed by our team shortly.</p>
              <p className="mt-2">Need help? <Link to="/support" className="text-blue-600 hover:underline">Contact Support</Link></p>
            </div>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default PaymentConfirm;
